import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Ev { id: string; kind: string; title: string; starts_at: string; state: string; source_id: string | null; confirmed_at: string | null; skipped: boolean; site_label: string | null }
interface Dose { id: string; scheduled_at: string; compound: string; dose_mg: string; route: string; needle_gauge: string | null; needle_length_mm: string | null }
interface Site { id: string; code: string; label: string; lastUsedAt: string | null }
interface Plan { id: string; due_on: string; reason: string; marker_codes: string[]; rationale_md: string | null; state: string }

const KIND_ICON: Record<string, string> = { dose: '💉', bloods: '🩸', scan: '📊', weigh_in: '⚖️', review: '📋', custom: '•' };

export function Calendar(): JSX.Element {
  const [events, setEvents] = useState<Ev[]>([]);
  const [upcoming, setUpcoming] = useState<Dose[]>([]);
  const [site, setSite] = useState<Site | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const reload = (): void => {
    void api<Ev[]>('/calendar').then(setEvents).catch(() => undefined);
    void api<Dose[]>('/calendar/upcoming').then(setUpcoming).catch(() => undefined);
    void api<{ recommended: Site | null }>('/calendar/next-site?route=im').then((r) => setSite(r.recommended)).catch(() => undefined);
    void api<Plan[]>('/calendar/blood-plans').then(setPlans).catch(() => undefined);
  };
  useEffect(reload, []);

  async function build(): Promise<void> {
    const r = await api<{ dosesCreated: number }>('/calendar/materialise', { method: 'POST' });
    setMsg(`${r.dosesCreated} scheduled items created for the next 45 days.`);
    reload();
  }

  async function confirm(id: string): Promise<void> {
    await api(`/calendar/doses/${id}/confirm`, {
      method: 'POST', body: JSON.stringify(site ? { siteId: site.id } : {}),
    });
    setMsg('Confirmed.');
    reload();
  }

  return (
    <>
      {msg && <div className="card"><p className="muted small">{msg}</p></div>}

      <div className="card">
        <h2>Due now</h2>
        {upcoming.length === 0 && <p className="muted">Nothing outstanding in the next 7 days.</p>}
        {site && upcoming.length > 0 && (
          <p className="muted small">
            Next site: <strong>{site.label}</strong>
            {site.lastUsedAt ? ` (last used ${new Date(site.lastUsedAt).toLocaleDateString()})` : ' (never used)'}.
            Rotation is not cosmetic, repeatedly hitting the same spot builds scar tissue.
          </p>
        )}
        {upcoming.map((d) => (
          <div key={d.id} className="subcard">
            <div className="crow">
              <div>
                <strong>{d.compound}</strong> {Number(d.dose_mg)}mg {d.route.toUpperCase()}
                <br /><span className="muted small">{new Date(d.scheduled_at).toLocaleString()}</span>
              </div>
              <button type="button" onClick={() => void confirm(d.id)}>Confirm</button>
            </div>
          </div>
        ))}
        <button type="button" className="ghost" onClick={() => void build()}>Rebuild schedule</button>
      </div>

      {plans.filter((p) => p.state === 'due').length > 0 && (
        <div className="card warn">
          <h2>Bloods due</h2>
          {plans.filter((p) => p.state === 'due').map((p) => (
            <div key={p.id} className="subcard">
              <strong>{new Date(p.due_on).toLocaleDateString()}</strong>{' '}
              <span className="chip">{p.reason.replace(/_/g, ' ')}</span>
              {p.rationale_md && <p className="small">{p.rationale_md}</p>}
              <p className="muted small">Ask for: {p.marker_codes.join(', ')}</p>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <h2>Calendar</h2>
        {events.length === 0 && <p className="muted">Nothing scheduled.</p>}
        <ul className="cal">
          {events.map((e) => (
            <li key={e.id} className={`cal-${e.state}`}>
              <span className="calicon">{KIND_ICON[e.kind] ?? '•'}</span>
              <span className="caldate">{new Date(e.starts_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
              <span className="caltitle">{e.title}{e.site_label ? <span className="muted small"> · {e.site_label}</span> : null}</span>
              <span className={`calstate ${e.state === 'done' ? 'good' : e.state === 'missed' ? 'bad' : 'muted'}`}>{e.state}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
